import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import loadable from '@loadable/component';
import { useDispatch, useSelector } from 'react-redux';
import {
    forIE,
    forTablet,
    forDesktop,
    isDesktop,
    forWideScreens,
    forExtraWideScreens,
} from '@westwing/ui-kit/breakPoints';
import useFeatureToggle from '../../hooks/useFeatureToggle';
import { FormattedImageData, FormattedMoodImageData, ProductInterface } from '../../redux/products/types';
import ErrorBoundary from '../Generic/ErrorBoundaries';
import StateInterface from '../../redux/types';
import useGa from '../../hooks/useGa';
import { setFromPLPWithMood, setPDPProductKnownInfoAction } from '../../redux/productdetail/actions';
import useElementIntersection from '../../hooks/useElementIntersection';
import useListingPageTracking from '../ListingPage/hooks/useListingPageTracking';
import { PageType, ProductListingPages } from '../../hooks/UrlManager/types';
import { useCurrentPageType } from '../../hooks/UrlManager/pageTypeHooks';
import HybridLink from '../Generic/Links/HybridLink';
import GenericProductImage from './GenericProductImage';
import ProductInfoBox from './ProductInfoBox';
import ProductBadges from './GenericProductBadges';
import { ProductList } from 'Common/ga/types';
import { safeRequestIdleCallback } from 'Common/helpers/viewport';
import { OverlayId } from 'Client/components/Overlays/types';
import { OVERLAY_QUERY_PARAM } from 'Client/components/Overlays/useOverlay';
import useBellaPush from 'Client/hooks/useBellaHistoryPush';
import { setIsMoodImageHovered } from 'Client/redux/products/actions';

const SkuDiverCheckbox = loadable(() => import('../ListingPage/SkuDiver/SkuDiverCheckbox'));

// calculated by (product cell height / product cell width) * 100%
const normalAspectRatio = '166%'; // comes from 357 x 594 images
const normalAspectRatioIncreased = '160.35%'; // comes from 357 x 567 images
const normalAspectRatioIncreasedWide = '159.10%'; // comes from 357 x 605 images
const heroAspectRatio = '130%'; // comes from 350 x 460 images

export const productGreyOverlay = css`
    &:after {
        position: absolute;
        content: ' ';
        width: 100%;
        height: 100%;
        background: ${({ theme }) => theme.colors.skeleton01};
        top: 0;
        left: 0;
        pointer-events: none;
    }
`;

export const productShadow = 'box-shadow: 0 2px 10px rgba(0, 0, 0, 0.14)';

export const StyledProductWrapper = styled.div<{
    isHero?: boolean;
    flexBasisDivisor?: number;
    showProductHoverEffect?: boolean;
    p100IncreasedDesktopPageWidth?: boolean;
}>`
    position: relative;
    width: 100%;

    &:before {
        display: block;
        content: '';
        width: 100%;
        padding-top: ${({ isHero }) => (isHero ? heroAspectRatio : normalAspectRatio)};
        ${forTablet`
            padding-top: ${normalAspectRatio};
        `}

        ${({ p100IncreasedDesktopPageWidth, isHero }) =>
            p100IncreasedDesktopPageWidth &&
            css`
                ${forWideScreens`
                    padding-top: ${isHero ? heroAspectRatio : normalAspectRatioIncreased};
                `}
                ${forExtraWideScreens`
                    padding-top: ${isHero ? heroAspectRatio : normalAspectRatioIncreasedWide};
                `}
            `}
    }

    ${({ showProductHoverEffect }) =>
        showProductHoverEffect
            ? css`
                  ${forDesktop`
                :hover {
                    ${productShadow};
                }
            `}
              `
            : ''}

    ${({ flexBasisDivisor }) => css`
        ${forIE`
            margin: 0 0 10px 0;
            flex-basis: calc( (100% - 20px) / ${flexBasisDivisor || 3} );
            padding-top: 0;
        `}
    `}
`;

export const StyledProductLink = styled(HybridLink)`
    text-decoration: none;
    top: 0;
    width: 100%;
    height: 100%;
    position: absolute;

    ${forIE`
        width: auto;
    `}

    ${productGreyOverlay}
`;

export const StyledProductLayout = styled.div`
    height: 100%;
    display: grid;
    grid-template-columns: 36px auto 29px;
    grid-template-rows: minmax(79px, auto) 1fr minmax(0px, auto);

    ${forIE`
        display: -ms-grid;
        -ms-grid-rows: 36px auto 90px;
        -ms-grid-columns:  36px minmax( 1fr , 300px ) 29px;
    `}
`;

export const StyledProductImageWrapper = styled.div`
    grid-column: 1/4;
    grid-row: 1/3;

    width: 100%;
    height: 100%;
    display: grid;
    grid-template:
        'image ' auto
        / 1fr;

    ${forIE`
        display: -ms-grid;
        -ms-grid-columns: 1fr;
        -ms-grid-rows: auto;
        -ms-grid-row : 1;
        -ms-grid-row-span : 2;
        -ms-grid-column : 1;
        -ms-grid-column-span : 3;
    `}
`;

export const RegularImageWrapper = styled.div`
    grid-area: image;

    ${forIE`
        -ms-grid-row : 1;
        -ms-grid-column : 1;
    `}
`;

const MoodImageWrapper = styled(RegularImageWrapper)<{ showMoodImage?: boolean }>`
    opacity: ${({ showMoodImage }) => (showMoodImage ? 1 : 0)};
`;

export interface GenericProductProps {
    className?: string;
    product: ProductInterface;
    isHeroProduct?: boolean;
    imageLoading?: 'lazy' | 'eager';
    imageSrc?: string;
    flexBasisDivisor?: number;
    showProductHoverEffect?: boolean;
}

const Product: React.FC<GenericProductProps> = React.memo(
    ({
        className,
        product,
        flexBasisDivisor,
        isHeroProduct = false,
        imageLoading = 'lazy',
        showProductHoverEffect = true,
    }) => {
        const gaTracking = useGa();
        const dispatch = useDispatch();
        const {
            skuDiver,
            p100PdpShowMoodImageFirst,
            p100ImageOnHoverPlp,
            p100PrlBoostDecrease,
            p100PrlBoostIncrease,
            p100CategoryBubblesDisabledDesktop,
            p100IncreasedDesktopPageWidth,
        } = useFeatureToggle();
        const isMoodImageHovered = useSelector<StateInterface, boolean | undefined>(
            state => state.products.isMoodImageHovered
        );
        const bellaPush = useBellaPush();
        const { trackPLPToPDP, trackPLPProductClicked } = useListingPageTracking();
        const [isHover, setIsHover] = useState(false);
        const { position, clickTrackingFn, productImage, productMoodImage } = product;
        const showMoodImage = !!(productMoodImage?.path && p100ImageOnHoverPlp && isHover);
        const strikeThroughColor = useSelector<StateInterface, string>(({ configurations }) => {
            return configurations.specialSales ? configurations.specialSales.strikeThroughColor : '';
        });
        const isProductValid = (productItem: ProductInterface): boolean => {
            const { sku, link, name } = productItem;
            return !!(sku && link && name);
        };
        const { pageType } = useCurrentPageType();
        const isListingPage = ProductListingPages.includes(pageType);

        const onIntersection = () => {
            if (isListingPage) {
                safeRequestIdleCallback(() => {
                    gaTracking.addImpression(product, {
                        list: ProductList.products,
                        position: position + 1, // Product impression should start from "1" not "0"
                    });
                });
            }
        };

        const handleClickActions = () => {
            if (isListingPage) {
                let relevantFeatureToggles: Record<string, boolean | undefined> = {};
                if (pageType !== PageType.LANDING) {
                    trackPLPToPDP('p100PrlBoostDecrease');
                    trackPLPToPDP('p100PrlBoostIncrease');
                    relevantFeatureToggles = { p100PrlBoostDecrease, p100PrlBoostIncrease, ...relevantFeatureToggles };
                }

                if (productMoodImage?.path) {
                    trackPLPToPDP('p100ImageOnHoverPlp');
                    relevantFeatureToggles = { p100ImageOnHoverPlp, ...relevantFeatureToggles };
                }

                if (isDesktop() && ![PageType.NEWPRODUCTS, PageType.ALLPRODUCTS].includes(pageType)) {
                    trackPLPToPDP('p100CategoryBubblesDisabledDesktop');
                    relevantFeatureToggles = { p100CategoryBubblesDisabledDesktop, ...relevantFeatureToggles };
                }

                trackPLPToPDP('p100IncreasedDesktopPageWidth');
                relevantFeatureToggles = { p100IncreasedDesktopPageWidth, ...relevantFeatureToggles };
                trackPLPProductClicked(relevantFeatureToggles);
            }

            if (clickTrackingFn) {
                clickTrackingFn();
            }
            dispatch(setFromPLPWithMood(!!isMoodImageHovered));
            dispatch(setPDPProductKnownInfoAction(product, !!p100PdpShowMoodImageFirst));
        };

        const handleEfficiencyBadgeClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
            e.stopPropagation();
            e.preventDefault();
            handleClickActions();
            bellaPush(product.link, false, {
                [OVERLAY_QUERY_PARAM]: OverlayId.ENERGY_EFFICIENCY,
            });
        };

        const impressionWrapperRef = useRef<HTMLDivElement>(null);

        useElementIntersection(impressionWrapperRef, () => {
            onIntersection();
        });

        const renderProductImage = (image: FormattedImageData | FormattedMoodImageData) => {
            return (
                <GenericProductImage
                    key={product.sku}
                    product={product}
                    imageData={image}
                    isHeroProduct={isHeroProduct}
                    loading={imageLoading}
                />
            );
        };

        useEffect(() => {
            if (!isMoodImageHovered && showMoodImage) {
                dispatch(setIsMoodImageHovered(true));
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [showMoodImage, isMoodImageHovered]);

        return (
            <ErrorBoundary boundaryName="Product" fallbackComponent={null}>
                <StyledProductWrapper
                    data-testid="generic-product"
                    className={className}
                    isHero={isHeroProduct}
                    ref={impressionWrapperRef}
                    flexBasisDivisor={flexBasisDivisor}
                    showProductHoverEffect={showProductHoverEffect}
                    p100IncreasedDesktopPageWidth={p100IncreasedDesktopPageWidth}
                >
                    {skuDiver && <SkuDiverCheckbox sku={product.simpleSku} />}

                    <StyledProductLink to={product.link} onClick={handleClickActions}>
                        {isProductValid(product) && (
                            <StyledProductLayout
                                onMouseEnter={() => setIsHover(true)}
                                onMouseLeave={() => setIsHover(false)}
                            >
                                <StyledProductImageWrapper>
                                    <RegularImageWrapper>{renderProductImage(productImage)}</RegularImageWrapper>
                                    {p100ImageOnHoverPlp && productMoodImage?.path && (
                                        <MoodImageWrapper showMoodImage={showMoodImage}>
                                            {renderProductImage(productMoodImage)}
                                        </MoodImageWrapper>
                                    )}
                                </StyledProductImageWrapper>
                                <ProductBadges product={product} />
                                <ProductInfoBox
                                    product={product}
                                    isHeroProduct={isHeroProduct}
                                    discountColor={strikeThroughColor}
                                    handleEfficiencyBadgeClick={handleEfficiencyBadgeClick}
                                />
                            </StyledProductLayout>
                        )}
                    </StyledProductLink>
                </StyledProductWrapper>
            </ErrorBoundary>
        );
    }
);

export default Product;
